import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Zap, FileText, Layout, Shuffle, Target, Users, 
  TrendingUp, Globe, ChevronRight, Check, Menu, X,
  Sun, Moon, Sparkles, Play, ArrowRight, ShieldCheck, Lock, BadgeCheck
} from 'lucide-react';
import CaseStudySection from '../components/CaseStudySection';
import TestimonialSection from '../components/TestimonialSection';
import BeforeAfterSection from '../components/BeforeAfterSection';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function LandingPage() {
  const { t, language, setLanguage, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState({ total_generations: 0, beta_users: 0 });

  // Fetch public platform stats for the hero social-proof counter.
  // Silent fail — landing page must never break if backend is slow / down.
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    axios
      .get(`${backend}/api/stats/public`, { timeout: 4000 })
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  const features = [
    { icon: Zap, key: 'hookGen', color: 'text-yellow-500' },
    { icon: FileText, key: 'scriptGen', color: 'text-blue-500' },
    { icon: Layout, key: 'storyboard', color: 'text-purple-500' },
    { icon: Shuffle, key: 'variations', color: 'text-green-500' },
    { icon: Target, key: 'competitor', color: 'text-red-500' },
    { icon: Users, key: 'creator', color: 'text-pink-500' },
    { icon: TrendingUp, key: 'predictor', color: 'text-orange-500' },
    { icon: Globe, key: 'multilang', color: 'text-cyan-500' }
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl">AIgnyx</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('features')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.features')}
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.pricing')}
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t('nav.faq')}
              </button>
            </div>

            {/* Right Side */}
            <div className="hidden md:flex items-center gap-4">
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="language-selector">
                    <Globe className="w-4 h-4 mr-2" />
                    {language.toUpperCase()}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {languages.map((lang) => (
                    <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)} data-testid={`lang-${lang.code}`}>
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Theme Toggle */}
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button data-testid="dashboard-btn">{t('nav.dashboard')}</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-btn">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="rounded-full" data-testid="get-started-btn">{t('nav.getStarted')}</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mobile-menu-overlay absolute top-16 left-0 right-0 p-4 border-b border-border"
          >
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('features')} className="text-left py-2">{t('nav.features')}</button>
              <button onClick={() => scrollToSection('pricing')} className="text-left py-2">{t('nav.pricing')}</button>
              <button onClick={() => scrollToSection('faq')} className="text-left py-2">{t('nav.faq')}</button>
              <div className="flex items-center gap-4 pt-4 border-t border-border">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Globe className="w-4 h-4 mr-2" />
                      {language.toUpperCase()}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {languages.map((lang) => (
                      <DropdownMenuItem key={lang.code} onClick={() => setLanguage(lang.code)}>
                        <span className="mr-2">{lang.flag}</span>
                        {lang.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" size="icon" onClick={toggleTheme}>
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
              </div>
              {isAuthenticated ? (
                <Link to="/dashboard" className="w-full">
                  <Button className="w-full">{t('nav.dashboard')}</Button>
                </Link>
              ) : (
                <div className="flex gap-2">
                  <Link to="/login" className="flex-1">
                    <Button variant="outline" className="w-full">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/signup" className="flex-1">
                    <Button className="w-full">{t('nav.getStarted')}</Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="hero-glow" />
        <div className="blob blob-primary w-96 h-96 -top-20 -right-20 opacity-20" />
        <div className="blob blob-accent w-64 h-64 top-40 -left-10 opacity-15" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm mb-8">
              <Sparkles className="w-4 h-4 text-primary" />
              <span>{t('hero.poweredBy')} GPT-5.2</span>
            </motion.div>
            
            <motion.h1 
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
              data-testid="hero-title"
            >
              <span className="gradient-text">{t('hero.title')}</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeUp}
              className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto"
            >
              {t('hero.subtitle')}
            </motion.p>
            
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-8 h-14 text-lg btn-press" data-testid="hero-cta">
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center">
                  <Play className="w-4 h-4 ml-0.5" />
                </div>
                {t('hero.secondary')}
              </button>
            </motion.div>

            {/* Social proof — real generations counter from MongoDB */}
            {stats.total_generations > 0 && (
              <motion.p
                variants={fadeUp}
                className="mt-6 text-sm text-muted-foreground flex items-center justify-center gap-2"
                data-testid="hero-stats-counter"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                <span>
                  <strong className="text-foreground font-semibold">
                    {stats.total_generations.toLocaleString()}+
                  </strong>{' '}
                  {t('hero.statsLine')}{' '}
                  <strong className="text-foreground font-semibold">
                    {stats.beta_users}
                  </strong>{' '}
                  {t('hero.statsBetaUsers')}
                </span>
              </motion.p>
            )}

            {/* Trust badges row — compliance + tech credibility */}
            <motion.div
              variants={fadeUp}
              className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-muted-foreground"
              data-testid="hero-trust-badges"
            >
              <div className="flex items-center gap-1.5" data-testid="badge-eu-ai-act">
                <BadgeCheck className="w-4 h-4 text-emerald-500" />
                <span>EU AI Act ✓</span>
              </div>
              <span className="opacity-40" aria-hidden="true">·</span>
              <div className="flex items-center gap-1.5" data-testid="badge-gdpr">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span>GDPR Compliant</span>
              </div>
              <span className="opacity-40" aria-hidden="true">·</span>
              <div className="flex items-center gap-1.5" data-testid="badge-ssl">
                <Lock className="w-4 h-4 text-emerald-500" />
                <span>SSL Secured</span>
              </div>
              <span className="opacity-40" aria-hidden="true">·</span>
              <div className="flex items-center gap-1.5" data-testid="badge-gpt">
                <Sparkles className="w-4 h-4 text-primary" />
                <span>Powered by GPT-5.2</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Hero Image/Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 lg:mt-24 relative"
          >
            <div className="relative rounded-2xl overflow-hidden border border-border/50 shadow-2xl bg-card">
              <div className="aspect-video bg-gradient-to-br from-secondary to-background p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-10 h-10 text-primary" />
                  </div>
                  <p className="text-muted-foreground">{t('hero.previewLabel')}</p>
                </div>
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent pointer-events-none" />
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              {t('howItWorks.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-lg">
              {t('howItWorks.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-3 gap-8"
          >
            {[1, 2, 3].map((step) => (
              <motion.div 
                key={step}
                variants={fadeUp}
                className="relative text-center p-8"
              >
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <span className="font-heading text-2xl font-bold text-primary">{step}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3">
                  {t(`howItWorks.step${step}.title`)}
                </h3>
                <p className="text-muted-foreground">
                  {t(`howItWorks.step${step}.desc`)}
                </p>
                {step < 3 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 w-8 h-8 text-muted-foreground/30" />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              {t('features.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t('features.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div 
                key={feature.key}
                variants={fadeUp}
                className="feature-card card-hover"
                data-testid={`feature-${feature.key}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-4 ${feature.color}`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg font-semibold mb-2">
                  {t(`features.${feature.key}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t(`features.${feature.key}.desc`)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Case study — real beta-user generation */}
      <CaseStudySection />

      {/* Testimonial */}
      <TestimonialSection />

      {/* Before / After */}
      <BeforeAfterSection />

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              {t('pricing.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-lg">
              {t('pricing.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {['starter', 'pro', 'agency'].map((plan, index) => (
              <motion.div 
                key={plan}
                variants={fadeUp}
                className={`relative bg-card rounded-3xl p-8 border ${
                  plan === 'pro' ? 'border-primary shadow-lg shadow-primary/10' : 'border-border'
                }`}
                data-testid={`pricing-${plan}`}
              >
                {plan === 'pro' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                    {t('pricing.pro.popular')}
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="font-heading text-xl font-semibold mb-2">
                    {t(`pricing.${plan}.name`)}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-heading text-4xl font-bold">{t(`pricing.${plan}.price`)}</span>
                    <span className="text-muted-foreground">{t(`pricing.${plan}.period`)}</span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  {t(`pricing.${plan}.features`).map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block">
                  <Button 
                    className={`w-full rounded-full ${plan === 'pro' ? '' : 'variant-outline'}`}
                    variant={plan === 'pro' ? 'default' : 'outline'}
                    data-testid={`pricing-${plan}-cta`}
                  >
                    {t('pricing.cta')}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 lg:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              {t('faq.title')}
            </motion.h2>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {[1, 2, 3, 4, 5].map((num) => (
              <AccordionItem key={num} value={`item-${num}`} className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  {t(`faq.q${num}.q`)}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {t(`faq.q${num}.a`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-primary/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold mb-6" data-testid="cta-title">
              {t('ctaSection.title')}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-lg text-muted-foreground mb-8" data-testid="cta-subtitle">
              {t('ctaSection.subtitle')}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-8 h-14 text-lg btn-press" data-testid="cta-get-started">
                  {t('nav.getStarted')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-xl">AIgnyx</span>
              </div>
              <p className="text-sm text-muted-foreground">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={() => scrollToSection('features')}>{t('footer.features')}</button></li>
                <li><button onClick={() => scrollToSection('pricing')}>{t('footer.pricing')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>
                  <a
                    href="mailto:hello@aignyx.com"
                    className="hover:text-primary break-all"
                    data-testid="footer-contact-email"
                  >
                    hello@aignyx.com
                  </a>
                </li>
                <li><Link to="/privacy" className="hover:text-primary">{t('footer.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-primary">{t('footer.terms')}</Link></li>
                <li><Link to="/cookies" className="hover:text-primary" data-testid="footer-cookies-link">{t('footer.cookies')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-border text-center text-sm text-muted-foreground">
            {t('footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
}
